/**
 * AI CONTEXT
 * Purpose: notification lifecycle debug page (create/read/archive/delete + eval comments).
 * Key deps: notification hooks, Firebase actions, EvaluationFormModal, ChangelogModal.
 * Referenced by: App route /notification-debug (debug-gated).
 * Importance: verifies notification pipeline behavior in real time.
 * Refactor: replace hard-coded IDs + batch destructive deletes.
 */
import {
  BellOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  MessageOutlined,
  NotificationOutlined,
} from "@ant-design/icons"
import { App, Button, Card, Col, Row, Statistic, Tag, Typography } from "antd"
import { sortBy } from "lodash"
import { useCallback, useMemo, useState } from "react"

import { evalCommentAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { EvalCommentConstructor } from "#customTypes/composedTypes/evalCommentTypes"
import {
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  NOTIFICATION_PRIORITY,
  NOTIFICATION_SUBTYPE,
  NOTIFICATION_TYPE,
  NotificationConstructor,
} from "#customTypes/composedTypes/notificationTypes"
import { DatabaseObject } from "#customTypes/types"
import {
  useArchivedNotifications,
  useNotificationActions,
  useUserNotifications,
} from "#hooks/jotai/useNotifications"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import DataDebugger from "#mocks/DataDebugger"

import ChangelogModal from "../Notifications/ChangelogModal"

import EvaluationFormModal from "./EvaluationView/EvaluationFormModal"
import { EvaluationFormContextProvider } from "./EvaluationView/useEvaluationFormContext"

const HARD_USER_ID = "aXsrxvD7sAOdfFo14wGm0trnZC52"
const HARD_STUDENT_ID = "qh5RC8zUa17nlfKLgy41"
const HARD_EVAL_ID = "qh5RC8zUa17nlfKLgy41-2026 A-core"

const { Title, Text } = Typography

const NotificationDebugDashboard = () => {
  const { message, modal } = App.useApp()
  const theme = useThemeContext()
  const { currentUserId } = useUserContext()

  const { notifications, unreadCount } = useUserNotifications(HARD_USER_ID)
  const archivedNotifications = useArchivedNotifications(HARD_USER_ID)
  const { toggleRead, markAllAsRead, archiveNotification, archiveAll } =
    useNotificationActions()

  const [showEvalModal, setShowEvalModal] = useState(false)
  const [showChangelog, setShowChangelog] = useState(false)

  const readCount = useMemo(
    () => notifications.filter((n) => n.read).length,
    [notifications]
  )

  const commentQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: { evaluationId: { value: HARD_EVAL_ID } },
    }),
    []
  )

  const rawComments = useFilteredDocs<EvalCommentConstructor>(
    evalCommentAtoms,
    DOC_TYPES.EVAL_COMMENTS,
    commentQuery
  )

  const evalComments = useMemo(
    () => sortBy(rawComments, (c) => c.createdAt),
    [rawComments]
  )

  const byType = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const n of notifications) {
      counts[n.type] = (counts[n.type] ?? 0) + 1
    }
    return counts
  }, [notifications])

  const byPriority = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const n of notifications) {
      counts[n.priority] = (counts[n.priority] ?? 0) + 1
    }
    return counts
  }, [notifications])

  const createTestNotification = useCallback(
    async (
      type: NOTIFICATION_TYPE,
      subtype: NOTIFICATION_SUBTYPE,
      priority: NOTIFICATION_PRIORITY,
      header: string,
      msg: string,
      metadata?: Record<string, unknown>
    ) => {
      const now = new Date().toISOString()
      const data: Omit<NotificationConstructor, "id"> = {
        recipientId: HARD_USER_ID,
        type,
        subtype,
        priority,
        header,
        message: msg,
        read: false,
        archived: false,
        metadata,
        createdAt: now,
        createdBy: HARD_USER_ID,
        lastUpdatedAt: now,
        lastUpdatedBy: HARD_USER_ID,
      }
      await Firebase.getInstance().addDocument<
        NotificationConstructor & DatabaseObject
      >(
        DOC_TYPES.NOTIFICATIONS,
        data as Omit<NotificationConstructor & DatabaseObject, "id">
      )
      void message.success("Notification created")
    },
    [message]
  )

  const handleDeleteAll = useCallback(() => {
    const allNotifs = [...notifications, ...archivedNotifications]
    if (allNotifs.length === 0) {
      void message.info("Nothing to delete")
      return
    }
    modal.confirm({
      title: "Delete ALL notifications?",
      content: `This will permanently delete ${allNotifs.length} notifications from Firestore. Cannot be undone.`,
      okText: "Delete All",
      okType: "danger",
      onOk: async () => {
        const fb = Firebase.getInstance()
        for (const n of allNotifs) {
          await fb.deleteDocument(DOC_TYPES.NOTIFICATIONS, n.id)
        }
        void message.success(`Deleted ${allNotifs.length} notifications`)
      },
    })
  }, [notifications, archivedNotifications, modal, message])

  const handleDeleteAllComments = useCallback(() => {
    if (evalComments.length === 0) {
      void message.info("No comments to delete")
      return
    }
    modal.confirm({
      title: "Delete ALL eval comments?",
      content: `This will permanently delete ${evalComments.length} comments for eval ${HARD_EVAL_ID}.`,
      okText: "Delete All",
      okType: "danger",
      onOk: async () => {
        const fb = Firebase.getInstance()
        for (const c of evalComments) {
          await fb.deleteDocument(DOC_TYPES.EVAL_COMMENTS, c.id)
        }
        void message.success(`Deleted ${evalComments.length} comments`)
      },
    })
  }, [evalComments, modal, message])

  return (
    <Flex
      col
      gap={16}
      style={{ padding: 40, maxWidth: 1400, margin: "0 auto" }}
    >
      <Title level={3}>
        <ExperimentOutlined /> Notification Debug Dashboard
      </Title>

      <Card size="small">
        <Flex gap={8} style={{ flexWrap: "wrap", alignItems: "center" }}>
          <Tag>Debug User: {HARD_USER_ID}</Tag>
          <Tag>Student: {HARD_STUDENT_ID}</Tag>
          <Tag>Eval: {HARD_EVAL_ID}</Tag>
        </Flex>
        <Flex
          gap={8}
          style={{ flexWrap: "wrap", alignItems: "center", marginTop: 8 }}
        >
          <Tag color={currentUserId === HARD_USER_ID ? "green" : "red"}>
            Bell queries as: {currentUserId ?? "null"}
          </Tag>
          {currentUserId !== HARD_USER_ID && (
            <Text type="danger" style={{ fontSize: 12 }}>
              Bell userId does not match debug userId — the bell won't show
              notifications created here unless you're logged in as{" "}
              {HARD_USER_ID}
            </Text>
          )}
        </Flex>
      </Card>

      {/* Live counters */}
      <Card title="Live Counts (real-time subscription)">
        <Row gutter={16}>
          <Col span={4}>
            <Statistic
              title="Total (non-archived)"
              value={notifications.length}
              prefix={<BellOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Unread"
              value={unreadCount}
              valueStyle={{ color: unreadCount > 0 ? theme.error : undefined }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Read"
              value={readCount}
              valueStyle={{ color: theme.eric.green }}
            />
          </Col>
          <Col span={4}>
            <Statistic title="Archived" value={archivedNotifications.length} />
          </Col>
          <Col span={4}>
            <Statistic
              title="Eval Comments"
              value={evalComments.length}
              prefix={<MessageOutlined />}
            />
          </Col>
        </Row>

        <Flex gap={24} style={{ marginTop: 16 }}>
          <Flex col gap={4}>
            <Text strong>By Type</Text>
            <Flex gap={8}>
              {Object.entries(byType).map(([type, count]) => (
                <Tag key={type}>
                  {type}: {count}
                </Tag>
              ))}
              {Object.keys(byType).length === 0 && (
                <Text type="secondary">none</Text>
              )}
            </Flex>
          </Flex>
          <Flex col gap={4}>
            <Text strong>By Priority</Text>
            <Flex gap={8}>
              {Object.entries(byPriority).map(([priority, count]) => (
                <Tag key={priority}>
                  {priority}: {count}
                </Tag>
              ))}
              {Object.keys(byPriority).length === 0 && (
                <Text type="secondary">none</Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>

      {/* Trigger buttons */}
      <Card title="Create Notifications">
        <Flex gap={12} style={{ flexWrap: "wrap" }}>
          <Button
            icon={<NotificationOutlined />}
            onClick={() =>
              createTestNotification(
                NOTIFICATION_TYPE.CHANGELOG,
                NOTIFICATION_SUBTYPE.CHANGELOG_NEW,
                NOTIFICATION_PRIORITY.NORMAL,
                "New features available",
                "Check out what's new in the latest update.",
                { changelogVersion: new Date().toISOString() }
              )
            }
          >
            Changelog (Normal)
          </Button>
          <Button
            icon={<MessageOutlined />}
            onClick={() =>
              createTestNotification(
                NOTIFICATION_TYPE.EVALUATION,
                NOTIFICATION_SUBTYPE.EVAL_COMMENT,
                NOTIFICATION_PRIORITY.NORMAL,
                "New eval comment",
                "Great progress on this student's evaluation!",
                { evaluationId: HARD_EVAL_ID }
              )
            }
          >
            Eval Comment (Normal)
          </Button>
          <Button
            icon={<NotificationOutlined />}
            style={{ borderColor: theme.warning, color: theme.warning }}
            onClick={() =>
              createTestNotification(
                NOTIFICATION_TYPE.EVALUATION,
                NOTIFICATION_SUBTYPE.EVAL_SUBMITTED,
                NOTIFICATION_PRIORITY.HIGH,
                "Eval submitted for review",
                "An evaluation has been submitted and needs your approval.",
                { evaluationId: HARD_EVAL_ID }
              )
            }
          >
            Eval Submitted (High)
          </Button>
          <Button
            icon={<NotificationOutlined />}
            danger
            onClick={() =>
              createTestNotification(
                NOTIFICATION_TYPE.CHANGELOG,
                NOTIFICATION_SUBTYPE.CHANGELOG_NEW,
                NOTIFICATION_PRIORITY.URGENT,
                "Critical update",
                "A critical system update has been deployed. Please review.",
                { changelogVersion: new Date().toISOString() }
              )
            }
          >
            Changelog (Urgent)
          </Button>
          <Button
            icon={<NotificationOutlined />}
            style={{ borderColor: theme.gray[5], color: theme.gray[5] }}
            onClick={() =>
              createTestNotification(
                NOTIFICATION_TYPE.EVALUATION,
                NOTIFICATION_SUBTYPE.EVAL_APPROVED,
                NOTIFICATION_PRIORITY.LOW,
                "Eval approved",
                "Your evaluation has been approved.",
                { evaluationId: HARD_EVAL_ID }
              )
            }
          >
            Eval Approved (Low)
          </Button>
        </Flex>
      </Card>

      {/* Bulk actions */}
      <Card title="Bulk Actions">
        <Flex gap={12} style={{ flexWrap: "wrap" }}>
          <Button
            onClick={() => void markAllAsRead(notifications)}
            disabled={unreadCount === 0}
          >
            Mark All Read ({unreadCount})
          </Button>
          <Button
            onClick={() => void archiveAll(notifications)}
            disabled={notifications.length === 0}
          >
            Archive All ({notifications.length})
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteAll}
            disabled={notifications.length + archivedNotifications.length === 0}
          >
            Delete All Notifications (
            {notifications.length + archivedNotifications.length})
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteAllComments}
            disabled={evalComments.length === 0}
          >
            Delete All Eval Comments ({evalComments.length})
          </Button>
          <Button
            type="primary"
            onClick={() => setShowEvalModal(true)}
            style={{ backgroundColor: theme.eric.blue }}
          >
            Open Eval Modal ({HARD_EVAL_ID})
          </Button>
          <Button onClick={() => setShowChangelog(true)}>
            Open Changelog Modal
          </Button>
        </Flex>
      </Card>

      <Row gutter={16}>
        {/* Notifications list */}
        <Col span={12}>
          <Flex col gap={16}>
            <Card
              title={`Active Notifications (${notifications.length})`}
              size="small"
              styles={{ body: { padding: 0 } }}
            >
              {notifications.length > 0 ? (
                <Flex col>
                  {notifications.map((n) => (
                    <Flex
                      key={n.id}
                      style={{
                        padding: "8px 12px",
                        borderBottom: "1px solid #f0f0f0",
                        gap: 8,
                        alignItems: "center",
                        background: n.read ? "transparent" : "#f6f9ff",
                        cursor: "pointer",
                      }}
                    >
                      {!n.read && (
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "#1677ff",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Flex col grow style={{ minWidth: 0, gap: 2 }}>
                        <Text strong ellipsis style={{ fontSize: 12 }}>
                          {n.header}
                        </Text>
                        <Text
                          type="secondary"
                          ellipsis
                          style={{ fontSize: 11 }}
                        >
                          {n.message}
                        </Text>
                        <Flex gap={4}>
                          <Tag
                            color="default"
                            style={{ fontSize: 10, margin: 0 }}
                          >
                            {n.type}
                          </Tag>
                          <Tag
                            color={
                              n.priority === "URGENT"
                                ? "red"
                                : n.priority === "HIGH"
                                  ? "orange"
                                  : "default"
                            }
                            style={{ fontSize: 10, margin: 0 }}
                          >
                            {n.priority}
                          </Tag>
                          <Tag
                            color={n.read ? "green" : "blue"}
                            style={{ fontSize: 10, margin: 0 }}
                          >
                            {n.read ? "READ" : "UNREAD"}
                          </Tag>
                        </Flex>
                      </Flex>
                      <Flex gap={4}>
                        <Button
                          size="small"
                          type="text"
                          onClick={() => void toggleRead(n)}
                        >
                          {n.read ? "Unread" : "Read"}
                        </Button>
                        <Button
                          size="small"
                          type="text"
                          danger
                          onClick={() => void archiveNotification(n.id)}
                        >
                          Archive
                        </Button>
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Text
                  type="secondary"
                  style={{ padding: 16, display: "block" }}
                >
                  No active notifications
                </Text>
              )}
            </Card>

            <Card
              title={`Archived Notifications (${archivedNotifications.length})`}
              size="small"
              styles={{ body: { padding: 0 } }}
            >
              {archivedNotifications.length > 0 ? (
                <DataDebugger data={archivedNotifications} />
              ) : (
                <Text
                  type="secondary"
                  style={{ padding: 16, display: "block" }}
                >
                  No archived notifications
                </Text>
              )}
            </Card>
          </Flex>
        </Col>

        {/* Raw data + eval comments */}
        <Col span={12}>
          <Flex col gap={16}>
            <Card
              title={`Raw Notification Data (${notifications.length})`}
              size="small"
              styles={{ body: { padding: 0 } }}
            >
              {notifications.length > 0 ? (
                <DataDebugger data={notifications} />
              ) : (
                <Text
                  type="secondary"
                  style={{ padding: 16, display: "block" }}
                >
                  No notification data
                </Text>
              )}
            </Card>

            <Card
              title={`Eval Comments for ${HARD_EVAL_ID} (${evalComments.length})`}
              size="small"
              styles={{ body: { padding: 0 } }}
            >
              {evalComments.length > 0 ? (
                <DataDebugger data={evalComments} />
              ) : (
                <Text
                  type="secondary"
                  style={{ padding: 16, display: "block" }}
                >
                  No eval comments
                </Text>
              )}
            </Card>
          </Flex>
        </Col>
      </Row>

      {showEvalModal && (
        <EvaluationFormContextProvider>
          <EvaluationFormModal
            evaluationId={HARD_EVAL_ID}
            onClose={() => setShowEvalModal(false)}
          />
        </EvaluationFormContextProvider>
      )}
      {showChangelog && (
        <ChangelogModal onClose={() => setShowChangelog(false)} />
      )}
    </Flex>
  )
}

export default NotificationDebugDashboard
